import { EmptyState, FlexColumn, Loading, MaterialIcon, useLoading, useModal } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { TABLE_COLUMNS_NAME } from '../../../constants/fleetMaintenanceTypes';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { apiParamsState } from '../../../storage/app';
import { FleetMaintenanceType } from '../../../types/fleet';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import { StyledTooltip } from '../../ui/StyledTooltip';
import TableEmptyActionCell from '../../ui/TableEmptyActionCell';
import useFleetMaintenanceTypes from '../hooks/useFleetMaintenanceTypes';
import FleetMaintenanceTypeModal from './FleetMaintenanceTypeModal';

function FleetMaintenanceTypeList() {
  const { theme, css, classes } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [maintenanceTypes, setMaintenanceTypes] = useState<FleetMaintenanceType[]>();
  const { page, onChangePage, loadFleetMaintenanceTypes, reload, search, onReload } = useFleetMaintenanceTypes();
  const [totalItems, setTotalItems] = useState(0);
  const { loading, stopLoading, startLoading } = useLoading(true);
  const { showFailFeedback } = useFeedback();
  const [selectedMaintenanceType, setSelectedMaintenanceType] = useState<FleetMaintenanceType>();
  const { openModal, closeModal, showModal } = useModal();

  useUpdateEffect(() => {
    onChangePage(0);
  }, [search, reload, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (page === 0) {
      onChangePage(1);
      return;
    }
    const loadData = async () => {
      startLoading();
      await loadMaintenanceTypes();
      stopLoading();
    };

    loadData();
  }, [page]);

  const loadMaintenanceTypes = async () => {
    const { types, error, total } = await loadFleetMaintenanceTypes();
    if (error) {
      showFailFeedback(error);
      setMaintenanceTypes(undefined);
      setTotalItems(0);
    } else {
      setMaintenanceTypes(types);
      setTotalItems(total ?? 0);
    }
  };

  const onOpenDetailModal = (maintenanceType: FleetMaintenanceType) => {
    setSelectedMaintenanceType(maintenanceType);
    openModal();
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!maintenanceTypes && !loading && <EmptyState title="Todavía no cuentas con ningún tipo de mantenimiento." />}
      {!maintenanceTypes && loading && <Loading />}
      {maintenanceTypes && (
        <>
          {!!maintenanceTypes?.length && (
            <StyledPaginatedTable
              columns={TABLE_COLUMNS_NAME}
              data={maintenanceTypes.map((maintenanceType) => {
                const canEdit = !!maintenanceType.client;
                return {
                  items: [
                    maintenanceType.value,
                    maintenanceType.description || '-',
                    maintenanceType.slug,
                    canEdit ? (
                      <StyledTooltip
                        key={`edit-${maintenanceType._id}`}
                        content="Editar"
                        showArrow={false}
                      >
                        <Button
                          size="mini"
                          shape="square"
                          onClick={() => onOpenDetailModal(maintenanceType)}
                          kind="tertiary"
                        >
                          <MaterialIcon
                            name="edit"
                            size={theme.sizing.scale700}
                          />
                        </Button>
                      </StyledTooltip>
                    ) : (
                      <TableEmptyActionCell />
                    ),
                  ],
                };
              })}
              totalItems={totalItems}
              page={page}
              onPageChange={onChangePage}
              loading={loading}
            />
          )}
          {!maintenanceTypes.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      <FleetMaintenanceTypeModal
        isOpen={showModal}
        onClose={(update) => {
          if (update) onReload();
          closeModal();
        }}
        selectedMaintenanceType={selectedMaintenanceType}
      />
    </FlexColumn>
  );
}

export default FleetMaintenanceTypeList;
